import { UserCallControl } from '../../app/types'
import { useTranslation } from 'react-i18next'
import React, { ReactElement, useMemo } from 'react'
import FormGroup from '../../common/form/FormGroup'
import ErrorAlert from '../../common/components/ErrorAlert'
import FormContainer from '../../common/form/FormContainer'
import { useForm, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useFormSubmission } from '../../common/servercall/useFormSubmission'
import * as yup from 'yup'
import { array, object } from 'yup'
import FormFieldBox from '../../common/form/FormFieldBox'
import FormCheckbox from '../../common/form/FormCheckbox'
import FormRow from '../../common/form/FormRow'
import { Box, Card, CardContent, CardHeader, Grid, Paper, Table, TableContainer, Typography } from '@mui/material'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import { CallControlNumberForm } from './callControlNumberForm'
import TextInput from '../../common/form/TextInput'
import { regexForNumberWithoutEmergencyNr } from '../../common/util/helpers'

const schema = () => yup.object().shape(
    {
        phoneNumberAnywhereList: array().of(
            object().shape({
                    number: yup.string().trim().max(22, 'too-long').required('required-m').matches(regexForNumberWithoutEmergencyNr, {
                        message: 'invalid-phone-number',
                        excludeEmptyString: true,
                    }).nullable()
                })
            )
        ,
        guardTimerSeconds: yup
            .number()
            .when(['useSystemDefaultGuardTimer', 'enableGuardTimer'], {
                is: (useSystemDefaultGuardTimer: boolean, enableGuardTimer: boolean) => !useSystemDefaultGuardTimer && enableGuardTimer,
                then: schema => yup.number().required('required-f').typeError('invalid-guard-timer').min(1, 'invalid-guard-timer').max(25, 'invalid-guard-timer'),
                otherwise: schema => yup.number().notRequired().nullable(),
            })
            .nullable(),
        numberConsecutiveBouncedCallsToForceAgentUnavailable: yup
            .number()
            .when(['useSystemDefaultUnavailableSettings', 'forceAgentUnavailableOnBouncedCallLimit'], {
                is: (useSystemDefaultUnavailableSettings: boolean, forceAgentUnavailableOnBouncedCallLimit: boolean) => !useSystemDefaultUnavailableSettings && forceAgentUnavailableOnBouncedCallLimit,
                then: schema => yup.number().required('required-f').typeError('invalid-bounce-limit').min(1, 'invalid-bounce-limit').max(5, 'invalid-bounce-limit'),
                otherwise: schema => yup.number().notRequired().nullable(),
            })
            .nullable(),
        wrapUpDestination: yup
            .string()
            .when('useSystemDefaultWrapUpDestination', {
                is: false,
                then: schema => yup.string().trim().max(22, 'too-long').matches(regexForNumberWithoutEmergencyNr, {
                    message: 'invalid-phone-number',
                    excludeEmptyString: true,
                }).nullable(),
                otherwise: schema => yup.string().notRequired().nullable(),
            })
            .nullable(),
    }
)

type Props = {
    callControl: UserCallControl
    onSubmit: (formData: UserCallControl) => void
    readonly?: boolean
    buildFormButtons: (isSubmitting: boolean) => ReactElement
}

export const CallControlForm = ({ callControl, onSubmit, readonly, buildFormButtons }: Props) => {
    const { t } = useTranslation()

    const { control, handleSubmit, setError, setValue, getFieldState } = useForm({
        mode: 'onSubmit',
        resolver: yupResolver(schema()),
        defaultValues: callControl
    })

    const { submit, isSubmitting, serverError, onError } = useFormSubmission<UserCallControl>(onSubmit, setError)

    const useSystemDefaultGuardTimer = useWatch({ control, name: 'useSystemDefaultGuardTimer' })
    const enableGuardTimer = useWatch({ control, name: 'enableGuardTimer' })
    const useSystemDefaultUnavailableSettings = useWatch({ control, name: 'useSystemDefaultUnavailableSettings' })
    const forceAgentUnavailableOnBouncedCallLimit = useWatch({ control, name: 'forceAgentUnavailableOnBouncedCallLimit' })
    const useSystemDefaultWrapUpDestination = useWatch({ control, name: 'useSystemDefaultWrapUpDestination' })

    const hasJoinedQueues = !!callControl.callCenterAvailabilities && callControl.callCenterAvailabilities.length > 0

    const acdStateOptions = useMemo(
        () => ['Sign-In', 'Sign-Out', 'Available', 'Unavailable', 'Wrap-Up'].map(v => ({
            value: v,
            label: t(`users.edit.agent-settings.acd-state-${v.toLowerCase().replace('-', '-')}`),
        })),
        [t]
    )

    return <>
        <ErrorAlert showAlert={!!serverError} />
            <FormContainer onSubmit={handleSubmit(submit, onError)}>
                <FormGroup label={t('users.edit.queue-availability.title')} fullWidth={true}>
                    <FormRow>
                        <FormFieldBox>
                            {hasJoinedQueues ? (
                                <TableContainer component={Paper} sx={{ mt: 1 }}>
                                    <Table sx={{ minWidth: 300 }} size={'small'}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell style={{ fontWeight: 700 }}>{t('flows.table.name')}</TableCell>
                                                <TableCell style={{ fontWeight: 700 }} align='right'>
                                                    {t('flows.detail.joined')}
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {callControl.callCenterAvailabilities.map((queue, index) => (
                                                <TableRow key={queue.callCenterId} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                    <TableCell component="th" scope="row">
                                                        {queue.name}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Box alignSelf={'center'} sx={{ marginLeft: 1 }}>
                                                            <FormCheckbox
                                                                label={''}
                                                                name={`callCenterAvailabilities[${index}].available`}
                                                                control={control}
                                                                setValue={setValue}
                                                                getFieldState={getFieldState}
                                                            />
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : <Typography>{t('users.edit.agent-settings.no-queues-joined')}</Typography>}
                        </FormFieldBox>
                    </FormRow>
                </FormGroup>
                <FormGroup label={t('users.edit.office-anywhere.title')} fullWidth={true}>
                    <FormRow>
                        <FormFieldBox>
                            <FormCheckbox
                                label={t('users.edit.office-anywhere.alert-all-locations')}
                                name={'alertAllLocations'}
                                control={control}
                                setValue={setValue}
                                getFieldState={getFieldState}
                            />
                        </FormFieldBox>
                    </FormRow>
                    <FormRow>
                        <FormFieldBox>
                            <CallControlNumberForm control={control} getFieldState={getFieldState} setValue={setValue}/>
                        </FormFieldBox>
                    </FormRow>
                </FormGroup>
                <FormGroup label={t('users.edit.agent-settings.title')} fullWidth={true}>
                    {!hasJoinedQueues &&
                        <FormRow>
                            <FormFieldBox fullWidth>
                                <Typography>{t('users.edit.agent-settings.no-queues-joined')}</Typography>
                            </FormFieldBox>
                        </FormRow>
                    }
                    {hasJoinedQueues && <>
                    <FormRow>
                        <FormFieldBox fullWidth>
                            <Card>
                                <CardHeader subheader={t('users.edit.agent-settings.acd-state')} />
                                <CardContent>
                                    <Grid container rowSpacing={2} p={1}>
                                        <FormRow>
                                            <FormFieldBox>
                                                <TextInput readonly={readonly} label={t('users.edit.agent-settings.acd-state-label')} name={'agentACDState'} control={control} required={false}
                                                           options={acdStateOptions} />
                                            </FormFieldBox>
                                        </FormRow>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </FormFieldBox>
                    </FormRow>
                    <FormRow>
                        <FormFieldBox fullWidth>
                            <Card>
                                <CardHeader subheader={t('settings.call-center-agent.guard-timer')} />
                                <CardContent>
                                <Grid container rowSpacing={2} p={1}>
                                    <FormRow>
                                        <FormFieldBox>
                                            <FormCheckbox
                                                label={t('settings.call-center-agent.use-system-default')}
                                                name={'useSystemDefaultGuardTimer'}
                                                control={control}
                                                setValue={setValue}
                                            />
                                        </FormFieldBox>
                                    </FormRow>
                                    {!useSystemDefaultGuardTimer &&
                                        <FormRow>
                                            <FormFieldBox>
                                                <FormCheckbox
                                                    label={t('settings.call-center-agent.guard-timer')}
                                                    name={'enableGuardTimer'}
                                                    control={control}
                                                    setValue={setValue}
                                                />
                                            </FormFieldBox>
                                            {enableGuardTimer &&
                                                <FormFieldBox>
                                                    <TextInput readonly={readonly}
                                                               label={t('settings.call-center-agent.guard-timer-seconds')}
                                                               name={'guardTimerSeconds'}
                                                               control={control} required={true} />
                                                </FormFieldBox>
                                            }
                                        </FormRow>
                                    }
                                </Grid>
                                </CardContent>
                            </Card>
                        </FormFieldBox>
                    </FormRow>
                    <FormRow>
                        <FormFieldBox fullWidth>
                            <Card>
                                <CardHeader subheader={t('settings.call-center-agent.unavailability-settings')} />
                                <CardContent>
                                <Grid container rowSpacing={2} p={1}>
                                    <FormRow>
                                        <FormFieldBox>
                                            <FormCheckbox
                                                label={t('settings.call-center-agent.use-system-default')}
                                                name={'useSystemDefaultUnavailableSettings'}
                                                control={control}
                                                setValue={setValue}
                                            />
                                        </FormFieldBox>
                                    </FormRow>
                                    {!useSystemDefaultUnavailableSettings &&
                                        [
                                            <FormRow>
                                                <FormFieldBox>
                                                    <FormCheckbox
                                                        label={t('settings.call-center-agent.force-au-on-dnd-activation')}
                                                        name={'forceAgentUnavailableOnDNDActivation'}
                                                        control={control}
                                                        setValue={setValue}
                                                    />
                                                </FormFieldBox>
                                            </FormRow>,
                                            <FormRow>
                                                <FormFieldBox>
                                                    <FormCheckbox
                                                        label={t('settings.call-center-agent.force-au-on-personal-calls')}
                                                        name={'forceAgentUnavailableOnPersonalCalls'}
                                                        control={control}
                                                        setValue={setValue}
                                                    />
                                                </FormFieldBox>
                                            </FormRow>,
                                            <FormRow>
                                                <FormFieldBox>
                                                    <FormCheckbox
                                                        label={t('settings.call-center-agent.force-au-on-not-reachable')}
                                                        name={'forceAgentUnavailableOnNotReachable'}
                                                        control={control}
                                                        setValue={setValue}
                                                    />
                                                </FormFieldBox>
                                            </FormRow>,
                                            <FormRow>
                                                <FormFieldBox>
                                                    <FormCheckbox
                                                        label={t('settings.call-center-agent.force-au-on-bounced-call-limit')}
                                                        name={'forceAgentUnavailableOnBouncedCallLimit'}
                                                        control={control}
                                                        setValue={setValue}
                                                    />
                                                </FormFieldBox>
                                                {forceAgentUnavailableOnBouncedCallLimit &&
                                                    <FormFieldBox>
                                                        <TextInput readonly={readonly}
                                                                   label={t('settings.call-center-agent.bounces')}
                                                                   name={'numberConsecutiveBouncedCallsToForceAgentUnavailable'}
                                                                   control={control} required={true} />
                                                    </FormFieldBox>
                                                }
                                            </FormRow>
                                        ]
                                    }
                                </Grid>
                                </CardContent>
                            </Card>
                        </FormFieldBox>
                    </FormRow>
                    <FormRow>
                        <FormFieldBox fullWidth>
                            <Card>
                                <CardHeader subheader={t('settings.call-center-agent.wrap-up-settings')} />
                                <CardContent>
                                <Grid container rowSpacing={2} p={1}>
                                    <FormRow>
                                        <FormFieldBox>
                                            <FormCheckbox
                                                label={t('settings.call-center-agent.use-system-default')}
                                                name={'useSystemDefaultWrapUpDestination'}
                                                control={control}
                                                setValue={setValue}
                                            />
                                        </FormFieldBox>
                                    </FormRow>
                                    {!useSystemDefaultWrapUpDestination &&
                                        <FormRow>
                                            <FormFieldBox>
                                                <TextInput readonly={readonly}
                                                           label={t('settings.call-center-agent.wrap-up-destination')}
                                                           name={'wrapUpDestination'}
                                                           control={control} required={false} />
                                            </FormFieldBox>
                                        </FormRow>
                                    }
                                </Grid>
                                </CardContent>
                            </Card>
                        </FormFieldBox>
                    </FormRow>
                    </>}
                </FormGroup>
                {buildFormButtons(isSubmitting)}
            </FormContainer>
        </>
}
